import React, { useMemo, useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FlowGrid, Button, Row, Separator, useMenuOverlay, Alert, Cell, LabelSpacer, useOverlay } from '@fs/zion-ui'
import ZionForm, { useFieldArray, useWatch, useZionForm, FormTextField, useFormState } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import { ContentAdd } from '@fs/zion-icon'
import getAdditionalFactDispatches from './utils/saveAdditionalFacts'
import { FullTextApp, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import HighlightButton from '../panels/shared/HighlightButton'
import { prepareFieldForHighlights } from '../shared/utils/highlightUtils'
import { getAdditionalFactOptions, prepareAdditionalFactDefaultValues } from './utils/additionalFacts.utils'
import FilterFactOverlay from './components/FilterFactOptionsOverlay'
import LinkedNamesOverlay from './components/LinkedNamesOverlay'
import ConfirmUnlinkName from './components/ConfirmUnlinkName'
import CardFooter from '../shared/components/CardFooter'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useDisplayHighlights } from '../../../hooks/useDisplayHighlights'

/**
 * AdditionalFactsCardEditMode component provides an editable interface for managing
 * additional facts associated with a person record. It includes form controls, validation,
 * highlighting capabilities, and support for linked names across multiple records.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - Person object containing record data
 * @param {string} props.person.id - Unique identifier for the person
 * @param {string} props.person.recordId - Record identifier for the person
 * @param {boolean} props.reviewed - Whether the card has been reviewed
 * @param {Function} props.onSave - Callback function invoked when saving changes with formValues, dispatches, and sls
 * @param {Function} props.setEditMode - Callback to update the edit mode state
 * @param {Array} props.additionalFacts - Array of additional fact objects to display and edit
 * @param {Function} [props.onDirtyChange] - Callback function invoked when form dirty state changes
 * @param {boolean} [props.saveCardDataToSls=false] - Whether to save card data to SLS backend
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @returns {React.ReactElement} The rendered edit mode form component
 */
const AdditionalFactsCardEditMode = ({
  person,
  reviewed,
  onSave,
  setEditMode,
  additionalFacts,
  onDirtyChange,
  saveCardDataToSls = false,
  splitScreenPartitionKey,
}) => {
  const [t] = useTranslation()
  const slsState = useSlsStateContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const { getHighlights, getDispatches, commitEdits, clearEdits } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const factOptions = useMemo(() => getAdditionalFactOptions(slsState.types), [slsState.types])
  const [selectedFact, setSelectedFact] = useState(null)
  const [personToUnlink, setPersonToUnlink] = useState(null)
  const [confirmedUnlinkList, setConfirmedUnlinkList] = useState([])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: prepareAdditionalFactDefaultValues(additionalFacts),
  })
  const fieldListOverlay = useOverlay()
  const confirmUnlinkOverlay = useOverlay()
  const linkedNamesOverlay = useMenuOverlay()
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)
  const slsDispatch = useSlsDispatchContext()

  const fieldArrayMethods = useFieldArray({
    name: 'additionalFacts',
    control: formMethods.control,
    keyName: 'formId', // to avoid conflict with 'id' from our data
  })
  const { fields } = fieldArrayMethods

  const watchedAdditionalFacts = useWatch({
    control: formMethods.control,
    name: 'additionalFacts',
  })

  const hasData = useMemo(() => {
    return fields.length > 0 && watchedAdditionalFacts.some((fact) => fact.value)
  }, [fields, watchedAdditionalFacts])

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Display highlights for all fields in edit mode
  const fieldIds = useMemo(() => fields.map((f) => f.id), [fields])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: !['editSharedHighlightPage1', 'editSharedHighlightPage2', 'highlightsPanel'].includes(topMostPanelId),
  })

  const onSubmit = async (formValues) => {
    try {
      // Generate dispatches - this will throw if validation fails
      const factsDispatches = getAdditionalFactDispatches({
        additionalFacts: formValues.additionalFacts,
        defaultValues: formMethods.formState.defaultValues,
        person,
        images: slsState.images,
        partitionKey: slsState.currentImageArk,
        highlights: getHighlights().mainEditHighlights,
        confirmedUnlinkList,
      })

      // Get highlight dispatches from the highlights context
      const highlightDispatches = getDispatches({
        mainImageArk: slsState.currentImageArk,
        splitScreenImageArk: splitScreenPartitionKey,
      })

      // Combine all dispatches
      const dispatches = [...factsDispatches, ...highlightDispatches]
      const sls = generateSls(dispatches)

      setSaving(true)

      if (saveCardDataToSls) {
        await saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          slsActionsSplunkDebugger,
          dispatch: slsDispatch,
          appName: FullTextApp,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            // Processing SLS dispatches to update local state even if the saveChangesFlag is off
            if (!saveChangesFlag.isOn) {
              slsDispatch({
                type: 'BATCH_DISPATCHES',
                value: dispatches,
              })
            }

            setSaving(false)
            onSave(formValues, dispatches, sls)
            commitEdits()
          },
          errorHandler: (err) => {
            setError(err)
            errorCollector(err, null, {
              codeId: 92,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
            setSaving(false)
          },
          saveInitiatorFilename: 'AdditionalFactsCard.js',
        })
      } else {
        // Processing SLS dispatches to update local state even if the saveChangesFlag is off
        if (!saveChangesFlag.isOn) {
          slsDispatch({
            type: 'BATCH_DISPATCHES',
            value: dispatches,
          })
        }

        setSaving(false)
        onSave(formValues, dispatches, sls)
        commitEdits()
      }
    } catch (err) {
      // Validation errors (duplicate relationships, missing data, etc.)
      setError(err)
      errorCollector(err, null, {
        codeId: 93,
        message: 'Additional Facts validation error',
      })
    }
  }

  const handleHighlightSave = useCallback(
    (fieldName, index) => (data) => {
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = formMethods.getValues(fieldName)
        const superElements = data.selectedPersonIds?.map((id) => ({ partitionKey: slsState.currentImageArk, id }))

        fieldArrayMethods.update(index, {
          ...currentFieldData,
          id: data.newFieldId,
          value: data.newFieldValue,
          dispatches: data?.dispatches || [],
          superElements,
        })
      }
    },
    [formMethods, fieldArrayMethods, slsState.currentImageArk]
  )

  return (
    <ZionForm methods={formMethods} onSubmit={onSubmit}>
      <Separator size="sm" />
      <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
        {fields.map((field, index) => (
          <div key={field.formId}>
            <Row guttersY="nano">
              <FormTextField
                name={`additionalFacts[${index}].value`}
                label={field.label}
                value={field.value}
                warningValidate={(val) =>
                  field.superElements?.length > 1 &&
                  val !== formMethods.formState.defaultValues?.additionalFacts?.[index]?.value
                    ? t(
                        'additional-facts-card.linked-field-change-warning',
                        'Changes will be made on all who are linked.'
                      )
                    : null
                }
                onChange={(ev) =>
                  formMethods.setValue(`additionalFacts[${index}]`, {
                    ...watchedAdditionalFacts[index],
                    value: ev.target.value,
                  })
                }
                disabled={!field.editable}
                growX
                dense={field?.superElements?.length > 1}
              />
              <Cell>
                <LabelSpacer />
                <HighlightButton
                  field={prepareFieldForHighlights(
                    formMethods.getValues,
                    field.key,
                    `additionalFacts[${index}]`,
                    slsState.types,
                    slsState.images
                  )}
                  splitScreenImageArk={splitScreenPartitionKey}
                  buttonProps={{ 'data-testid': `${field.key}-highlight-button` }}
                  linkName={`additional-fact-${field.key}-highlight`}
                  disabled={!field.editable}
                  onSave={handleHighlightSave(`additionalFacts.${index}`, index)}
                  saveToSls={!field.added}
                  growX
                  iconOnly
                />
              </Cell>
            </Row>
            {field.superElements?.length > 1 && (
              <Button
                {...linkedNamesOverlay.menuButtonProps}
                data-testid={`${field.key}-view-linked-names-button`}
                linkName="Additional Facts View Linked Names"
                onClick={(event) => {
                  linkedNamesOverlay.open(event)
                  setSelectedFact(field)
                }}
              >
                {t('additional-facts-card.view-linked-names', 'View Linked Names to Fact')}
              </Button>
            )}
          </div>
        ))}
        <Button
          Icon={ContentAdd}
          emphasis="medium"
          data-testid="add-additional-fact-button"
          linkName="Additional Facts Add Facts Button"
          onClick={(event) => fieldListOverlay.open(event)}
        >
          {t('additional-facts-card.additional-facts.btn', 'Additional Facts')}
        </Button>
      </FlowGrid>
      {person && selectedFact && (
        <LinkedNamesOverlay
          person={person}
          overlay={linkedNamesOverlay}
          fact={selectedFact}
          selectHandler={(personEl) => {
            setPersonToUnlink(personEl)
            confirmUnlinkOverlay.open()
            linkedNamesOverlay.close()
          }}
        />
      )}
      <FilterFactOverlay
        fieldListOverlay={fieldListOverlay}
        factOptions={factOptions}
        fieldArrayMethods={fieldArrayMethods}
      />
      <ConfirmUnlinkName
        overlay={confirmUnlinkOverlay}
        person={personToUnlink}
        onRemove={() => setConfirmedUnlinkList((prev) => [...prev, { person: personToUnlink, fact: selectedFact }])}
      />
      {error && (
        <Alert type="error">
          {t('additional-facts-card.save-error-msg', 'Error saving data. Please try again later.')}
        </Alert>
      )}
      <Separator size="sm" />
      <CardFooter
        reviewed={reviewed}
        hasData={hasData}
        saving={saving}
        onCancel={() => {
          onDirtyChange?.(false)
          setEditMode(false)
          clearEdits()
        }}
        label="additional-facts"
        customLinkNamePrefix="Additional Facts Footer"
      />
    </ZionForm>
  )
}

AdditionalFactsCardEditMode.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recordId: PropTypes.string.isRequired,
  }).isRequired,
  reviewed: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  setEditMode: PropTypes.func.isRequired,
  additionalFacts: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
      superElements: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ).isRequired,
  onDirtyChange: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
}

export default AdditionalFactsCardEditMode
